import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ExternalLink, RefreshCw, Settings, ArrowRightLeft } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { subscriptionService } from '@/services/api/subscription.service';
import { organizationService } from '@/services/api';
import { formatCurrency, type Currency } from '@/utils/currency';
import { PricingTable } from '@/components/subscription/PricingTable';

export default function BillingPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation('billing');
  const { t: tPlans } = useTranslation('plans');

  // Helper to get translated plan name from plan code
  const getTranslatedPlanName = (planCode?: string, fallbackName?: string) => {
    if (!planCode) return fallbackName || '';
    const key = `${planCode.toLowerCase()}.name`;
    const translated = tPlans(key);
    // If translation key not found, i18next returns the key itself
    return translated !== key ? translated : (fallbackName || planCode);
  };

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isOpeningSwitchPlan, setIsOpeningSwitchPlan] = useState(false);

  useSetPageHeader({ title: t('title'), sub: t('subtitle') }, [t]);

  // Fetch current subscription
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: () => subscriptionService.getCurrentSubscription(),
  });

  // Organization billing currency drives the pricing table + the whole payment flow.
  // It's fixed at signup from the visitor's IP and never changes with the display currency.
  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: () => {
      return subscriptionService.cancelSubscription(false);
    },
    onSuccess: () => {
      toast({
        title: t('toasts.subscriptionCanceled'),
        description: t('toasts.subscriptionCanceledMessage'),
      });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      setShowCancelDialog(false);
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.cancellationFailed'),
        description: error.message || t('toasts.cancellationFailedDescription'),
        variant: 'destructive',
      });
    },
  });

  // Resume subscription mutation
  const resumeMutation = useMutation({
    mutationFn: () => {
      return subscriptionService.resumeSubscription();
    },
    onSuccess: () => {
      toast({
        title: t('toasts.subscriptionResumed'),
        description: t('toasts.subscriptionResumedMessage'),
      });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.resumeFailed'),
        description: error.message || t('toasts.resumeFailedDescription'),
        variant: 'destructive',
      });
    },
  });

  const subscription = subscriptionData?.subscription;

  // Helper function to safely format dates
  const safeFormatDate = (dateString: string | null | undefined, formatStr: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return format(date, formatStr);
    } catch (error) {
      console.error('Date formatting error:', error, 'for date:', dateString);
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any }> = {
      active: { variant: 'default' },
      trialing: { variant: 'secondary' },
      past_due: { variant: 'destructive' },
      canceled: { variant: 'secondary' },
      incomplete: { variant: 'destructive' },
    };

    const config = variants[status] || { variant: 'default' };
    const label = t(`currentPlan.status.${status}`, status);
    return <Badge variant={config.variant}>{label}</Badge>;
  };

  const handleOpenBillingPortal = async () => {
    setIsOpeningPortal(true);
    try {
      const url = await subscriptionService.getBillingPortalUrl(i18n.language);
      window.open(url, '_blank');
    } catch (error: any) {
      toast({
        title: t('stripePortal.error'),
        description: error.message || t('toasts.portalOpenError'),
        variant: 'destructive',
      });
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const handleOpenSwitchPlan = async () => {
    setIsOpeningSwitchPlan(true);
    try {
      const url = await subscriptionService.getPlanChangePortalUrl(i18n.language);
      window.open(url, '_blank');
    } catch (error: any) {
      toast({
        title: t('stripePortal.error'),
        description: error.message || t('toasts.planChangeError'),
        variant: 'destructive',
      });
    } finally {
      setIsOpeningSwitchPlan(false);
    }
  };

  if (isLoadingSubscription) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Current Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('currentPlan.title')}</CardTitle>
              <CardDescription>{t('currentPlan.subtitle')}</CardDescription>
            </div>
            {subscription && getStatusBadge(subscription.status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!subscription || subscription.planId === 'free' ? (
            <div className="text-center py-8">
              <p className="text-body text-soft">
                {t('currentPlan.freePlan')}
              </p>
              <p className="text-body-sm text-soft mt-2">
                {t('currentPlan.upgradeBelow', 'Choose a plan below to unlock more features')}
              </p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft mb-1">{t('currentPlan.planName')}</p>
                  <p className="text-h2 text-ink">{getTranslatedPlanName(subscription.planCode, subscription.planName)}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft mb-1">{t('currentPlan.billingCycle')}</p>
                  <p className="text-h2 text-ink">{t(`currentPlan.cycles.${subscription.billingCycle}`, subscription.billingCycle)}</p>
                </div>
              </div>

              {subscription.status === 'trialing' && subscription.trialEnd && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t('currentPlan.trialActive')}</AlertTitle>
                  <AlertDescription>
                    {t('currentPlan.trialEndsOn', { date: safeFormatDate(subscription.trialEnd, 'MMM d, yyyy') })}
                  </AlertDescription>
                </Alert>
              )}

              {subscription.cancelAt && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t('currentPlan.subscriptionCanceled')}</AlertTitle>
                  <AlertDescription>
                    {t('currentPlan.subscriptionWillEnd', { date: safeFormatDate(subscription.cancelAt, 'MMM d, yyyy') })}
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid md:grid-cols-2 gap-6 pt-4 border-t">
                {subscription.currentPeriodStart && subscription.currentPeriodEnd && (
                  <div>
                    <p className="text-mono-eyebrow uppercase text-soft mb-1">{t('currentPlan.currentPeriod')}</p>
                    <p className="font-medium">
                      {safeFormatDate(subscription.currentPeriodStart, 'MMM d, yyyy')} -{' '}
                      {safeFormatDate(subscription.currentPeriodEnd, 'MMM d, yyyy')}
                    </p>
                  </div>
                )}
                {subscriptionData.nextBillingDate && subscriptionData.nextBillingAmount !== null && subscriptionData.nextBillingAmount !== undefined && (
                  <div>
                    <p className="text-mono-eyebrow uppercase text-soft mb-1">{t('currentPlan.nextBilling')}</p>
                    <p className="font-medium">
                      {formatCurrency(subscriptionData.nextBillingAmount, (subscriptionData.nextBillingCurrency || 'USD') as Currency)} {t('currentPlan.on')}{' '}
                      {safeFormatDate(subscriptionData.nextBillingDate, 'MMM d, yyyy')}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
        {subscription && subscription.planId !== 'free' && (
          <CardFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {!subscription.cancelAt && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleOpenSwitchPlan}
                    disabled={isOpeningSwitchPlan}
                    className="flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px]"
                  >
                    {isOpeningSwitchPlan ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        {t('currentPlan.switchingPlan', 'Loading...')}
                      </>
                    ) : (
                      <>
                        <ArrowRightLeft className="w-4 h-4" />
                        {t('currentPlan.switchPlan', 'Switch plan')}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleOpenBillingPortal}
                    disabled={isOpeningPortal}
                    className="flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px]"
                  >
                    {isOpeningPortal ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        {t('stripePortal.loading')}
                      </>
                    ) : (
                      <>
                        <Settings className="w-4 h-4" />
                        {t('currentPlan.manageSubscription', 'Manage subscription')}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowCancelDialog(true)}
                    className="text-red-600 hover:text-red-700 w-full sm:w-auto min-h-[44px]"
                  >
                    {t('currentPlan.cancelSubscription')}
                  </Button>
                </>
              )}
              {subscription.cancelAt && (
                <Button
                  variant="primary"
                  onClick={() => resumeMutation.mutate()}
                  disabled={resumeMutation.isPending}
                  className="w-full sm:w-auto min-h-[44px]"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('currentPlan.resumeSubscription')}
                </Button>
              )}
            </div>
          </CardFooter>
        )}
      </Card>

      {/* Available Plans Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('availablePlans.title', 'Available Plans')}</CardTitle>
          <CardDescription>
            {t('availablePlans.subtitle', 'Choose the plan that best fits your needs')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Show PricingTable for all users - free users get "Start your free trial", subscribed users get "Switch plan" */}
          <PricingTable
            currentPriceId={subscription?.planId}
            currency={(organization?.billing_currency || 'eur').toLowerCase()}
            interval={subscription?.billingCycle === 'monthly' ? 'month' : 'year'}
            isSubscribed={!!(subscription && (subscription.status === 'active' || subscription.status === 'trialing'))}
          />
        </CardContent>
      </Card>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dialogs.cancelSubscription.title')}</DialogTitle>
            <DialogDescription>
              {t('dialogs.cancelSubscription.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              {t('dialogs.cancelSubscription.keepSubscription')}
            </Button>
            <Button
              variant="danger"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? t('dialogs.cancelSubscription.canceling') : t('dialogs.cancelSubscription.confirmCancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
